import React, { useState } from 'react';
import {
  SunIcon, ArrowTrendingUpIcon, SparklesIcon, ArrowLongRightIcon, ArrowPathIcon, ExclamationTriangleIcon, HomeIcon, InformationCircleIcon
} from '@heroicons/react/24/outline';

const DIRECTIONS = [
  "East", "West", "North", "South", "Northeast", "Northwest", "Southeast", "Southwest"
];

const VASTU_RECOMMENDATIONS = {
  "East": {
    label: "East Facing",
    rooms: [
      { name: "Living Room", zone: "East", tip: "Brings in morning sunlight, creates energetic start to the day." },
      { name: "Master Bedroom", zone: "South-West", tip: "Ensures stability, strength, and restful sleep." },
      { name: "Kitchen", zone: "South-East", tip: "Symbolizes fire; this zone maintains energy and digestion." },
      { name: "Bathroom", zone: "North-West", tip: "Allows water drainage and avoids energy stagnation." },
      { name: "Pooja Room", zone: "North-East", tip: "Spiritual energy thrives in the Ishanya (NE) corner." },
      { name: "Store Room", zone: "South", tip: "Keeps unused items grounded and out of main airflow." },
      { name: "Study Room", zone: "East", tip: "Promotes clarity, wisdom, and concentration." },
      { name: "Guest Bedroom", zone: "North-West", tip: "Welcoming and semi-private for guests." },
    ]
  },
  "West": {
    label: "West Facing",
    rooms: [
      { name: "Living Room", zone: "West", tip: "Evening light creates a warm, relaxing atmosphere." },
      { name: "Master Bedroom", zone: "North-East", tip: "Brings calmness and mental clarity." },
      { name: "Kitchen", zone: "North-West", tip: "Ensures proper ventilation and energy flow." },
      { name: "Bathroom", zone: "South-East", tip: "Keeps water away from the fire zone, maintaining balance." },
      { name: "Pooja Room", zone: "North-East", tip: "Spiritual energy thrives in the Ishanya (NE) corner." },
      { name: "Store Room", zone: "North", tip: "Keeps storage cool and accessible." },
      { name: "Study Room", zone: "West", tip: "Evening light aids focus and productivity." },
      { name: "Guest Bedroom", zone: "South-East", tip: "Comfortable and accessible for guests." },
    ]
  },
  "North": {
    label: "North Facing",
    rooms: [
      { name: "Living Room", zone: "North", tip: "Brings in positive magnetic energy and prosperity." },
      { name: "Master Bedroom", zone: "South-West", tip: "Ensures stability, strength, and restful sleep." },
      { name: "Kitchen", zone: "South-East", tip: "Symbolizes fire; this zone maintains energy and digestion." },
      { name: "Bathroom", zone: "North-West", tip: "Allows water drainage and avoids energy stagnation." },
      { name: "Pooja Room", zone: "North-East", tip: "Spiritual energy thrives in the Ishanya (NE) corner." },
      { name: "Store Room", zone: "West", tip: "Keeps storage away from main living areas." },
      { name: "Study Room", zone: "North", tip: "Promotes clarity, wisdom, and concentration." },
      { name: "Guest Bedroom", zone: "North-West", tip: "Welcoming and semi-private for guests." },
    ]
  },
  "South": {
    label: "South Facing",
    rooms: [
      { name: "Living Room", zone: "South", tip: "Strong structure, good for gatherings and warmth." },
      { name: "Master Bedroom", zone: "North-West", tip: "Balances the heat from the south, ensures comfort." },
      { name: "Kitchen", zone: "North-West", tip: "Keeps fire away from the main wind direction." },
      { name: "Bathroom", zone: "South-East", tip: "Keeps water away from the fire zone, maintaining balance." },
      { name: "Pooja Room", zone: "North-East", tip: "Spiritual energy thrives in the Ishanya (NE) corner." },
      { name: "Store Room", zone: "East", tip: "Keeps storage accessible and ventilated." },
      { name: "Study Room", zone: "South", tip: "Warmth and light aid focus and learning." },
      { name: "Guest Bedroom", zone: "South-East", tip: "Comfortable and accessible for guests." },
    ]
  },
  "Northeast": {
    label: "North-East Facing",
    rooms: [
      { name: "Living Room", zone: "North-East", tip: "Brings in spiritual sunlight and serene breeze." },
      { name: "Master Bedroom", zone: "South-West", tip: "Ensures stability, strength, and restful sleep." },
      { name: "Kitchen", zone: "East", tip: "Receives morning sun, ideal for cooking." },
      { name: "Bathroom", zone: "West", tip: "Keeps water away from main living areas." },
      { name: "Pooja Room", zone: "North-East", tip: "Spiritual energy thrives in the Ishanya (NE) corner." },
      { name: "Store Room", zone: "South", tip: "Keeps unused items grounded and out of main airflow." },
      { name: "Study Room", zone: "East", tip: "Promotes clarity, wisdom, and concentration." },
      { name: "Guest Bedroom", zone: "North", tip: "Welcoming and cool for guests." },
    ]
  },
  "Northwest": {
    label: "North-West Facing",
    rooms: [
      { name: "Living Room", zone: "North-West", tip: "Ensures good airflow and social energy." },
      { name: "Master Bedroom", zone: "South", tip: "Balances the wind, ensures restful sleep." },
      { name: "Kitchen", zone: "South-East", tip: "Symbolizes fire; this zone maintains energy and digestion." },
      { name: "Bathroom", zone: "East", tip: "Keeps water away from main living areas." },
      { name: "Pooja Room", zone: "East", tip: "Receives morning sun, ideal for prayer." },
      { name: "Store Room", zone: "South-West", tip: "Keeps storage away from main living areas." },
      { name: "Study Room", zone: "North", tip: "Promotes clarity, wisdom, and concentration." },
      { name: "Guest Bedroom", zone: "South-East", tip: "Comfortable and accessible for guests." },
    ]
  },
  "Southeast": {
    label: "South-East Facing",
    rooms: [
      { name: "Living Room", zone: "South-East", tip: "Receives early sunlight, energizing the space." },
      { name: "Master Bedroom", zone: "West", tip: "Balances the fire zone, ensures restful sleep." },
      { name: "Kitchen", zone: "East", tip: "Receives morning sun, ideal for cooking." },
      { name: "Bathroom", zone: "West", tip: "Keeps water away from main living areas." },
      { name: "Pooja Room", zone: "East", tip: "Receives morning sun, ideal for prayer." },
      { name: "Store Room", zone: "South", tip: "Keeps unused items grounded and out of main airflow." },
      { name: "Study Room", zone: "North-East", tip: "Promotes clarity, wisdom, and concentration." },
      { name: "Guest Bedroom", zone: "South", tip: "Welcoming and cool for guests." },
    ]
  },
  "Southwest": {
    label: "South-West Facing",
    rooms: [
      { name: "Living Room", zone: "South-West", tip: "Strong foundation, ideal for family gatherings." },
      { name: "Master Bedroom", zone: "East", tip: "Receives morning sun, ensures a fresh start." },
      { name: "Kitchen", zone: "North-West", tip: "Ensures proper ventilation and energy flow." },
      { name: "Bathroom", zone: "East", tip: "Keeps water away from main living areas." },
      { name: "Pooja Room", zone: "North-East", tip: "Spiritual energy thrives in the Ishanya (NE) corner." },
      { name: "Store Room", zone: "West", tip: "Keeps storage away from main living areas." },
      { name: "Study Room", zone: "South-East", tip: "Warmth and light aid focus and learning." },
      { name: "Guest Bedroom", zone: "East", tip: "Welcoming and cool for guests." },
    ]
  }
};

const PRINCIPLES = [
  { icon: "☀️", title: "Sun Path Optimization", desc: "Morning light distribution for health and energy." },
  { icon: "🌬", title: "Airflow Management", desc: "Passive cooling and heating for comfort." },
  { icon: "🧲", title: "Magnetic Axis Planning", desc: "Aligns structure for health and balance." },
  { icon: "🔥", title: "Thermal Zoning", desc: "Keeps rooms at optimal temperatures." },
  { icon: "🏠", title: "Room Placement Science", desc: "Blends vastu with ergonomic living." },
];

function Compass({ direction }) {
  const angles = {
    "North": 0, "Northeast": 45, "East": 90, "Southeast": 135,
    "South": 180, "Southwest": 225, "West": 270, "Northwest": 315
  };
  const angle = angles[direction] || 0;
  return (
    <div className="flex flex-col items-center mb-4">
      <div className="relative w-40 h-40">
        <svg width="100%" height="100%" viewBox="0 0 120 120" fill="none">
          <circle cx="60" cy="60" r="54" stroke="#FBBF24" strokeWidth="6" fill="#FEF9C3" />
          <g style={{ transform: `rotate(${angle}deg)`, transformOrigin: '60px 60px', transition: 'transform 0.8s cubic-bezier(.23,1.01,.32,1)' }}>
            <polygon points="60,20 68,60 60,50 52,60" fill="#2563eb" />
          </g>
          <text x="60" y="18" textAnchor="middle" fontSize="16" fill="#B45309">N</text>
          <text x="60" y="112" textAnchor="middle" fontSize="16" fill="#B45309">S</text>
          <text x="10" y="66" textAnchor="middle" fontSize="16" fill="#B45309">W</text>
          <text x="110" y="66" textAnchor="middle" fontSize="16" fill="#B45309">E</text>
        </svg>
      </div>
      {direction && (
        <div className="mt-2 text-lg font-bold text-yellow-900 text-center">
          Your plot is <span className="text-palamuru-blue">{direction}</span> facing
        </div>
      )}
    </div>
  );
}

function Tooltip({ text }) {
  return (
    <span className="relative group cursor-pointer">
      <InformationCircleIcon className="w-4 h-4 text-blue-400 inline ml-1" />
      <span className="absolute left-1/2 -translate-x-1/2 mt-2 w-48 bg-gray-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 z-10 pointer-events-none transition">
        {text}
      </span>
    </span>
  );
}

function HouseLayout({ direction }) {
  // Map direction to SVG highlight coordinates
  const highlights = {
    East:   <rect x="160" y="20" width="40" height="120" fill="#fde68a" opacity="0.7" rx="16" />,
    West:   <rect x="20" y="20" width="40" height="120" fill="#fde68a" opacity="0.7" rx="16" />,
    North:  <rect x="20" y="20" width="180" height="40" fill="#fde68a" opacity="0.7" rx="16" />,
    South:  <rect x="20" y="100" width="180" height="40" fill="#fde68a" opacity="0.7" rx="16" />,
    Northeast: <polygon points="110,20 200,20 200,80 160,80 110,60" fill="#fde68a" opacity="0.7" />,
    Northwest: <polygon points="20,20 110,20 110,60 60,80 20,80" fill="#fde68a" opacity="0.7" />,
    Southeast: <polygon points="110,100 200,100 200,140 110,140 110,120" fill="#fde68a" opacity="0.7" />,
    Southwest: <polygon points="20,100 110,100 110,120 60,140 20,140" fill="#fde68a" opacity="0.7" />,
  };
  // Label positions for each direction
  const labelPos = {
    East:     { x: 180, y: 100 },
    West:     { x: 40,  y: 100 },
    North:    { x: 110, y: 40 },
    South:    { x: 110, y: 140 },
    Northeast:{ x: 170, y: 60 },
    Northwest:{ x: 60,  y: 60 },
    Southeast:{ x: 170, y: 130 },
    Southwest:{ x: 60,  y: 130 },
  };
  return (
    <div className="flex justify-center my-6">
      <svg width="220" height="160" viewBox="0 0 220 160">
        <rect x="20" y="20" width="180" height="120" rx="16" fill="#f3f4f6" stroke="#cbd5e1" strokeWidth="3" />
        {highlights[direction]}
        {direction && (
          <text x={labelPos[direction].x} y={labelPos[direction].y} textAnchor="middle" fontSize="14" fill="#b45309" fontWeight="bold">
            {direction}
          </text>
        )}
      </svg>
    </div>
  );
}

export default function MyVastuPage() {
  const [selected, setSelected] = useState(null);
  const vastu = selected ? VASTU_RECOMMENDATIONS[selected] : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-blue-50 to-white py-8 px-2 sm:px-6">
      <div className="max-w-4xl mx-auto">
        {/* Direction Selector */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl md:text-4xl font-serif text-yellow-900 mb-2 tracking-tight">My Vastu</h1>
          <p className="text-lg text-gray-700 mb-4">Select your plot's facing direction to see personalized vastu insights.</p>
          <div className="flex flex-wrap justify-center gap-3 mb-4">
            {DIRECTIONS.map(dir => (
              <button
                key={dir}
                className={`px-4 py-2 rounded-xl border font-semibold focus:outline-none focus:ring-2 focus:ring-blue-200 transition-all text-base ${selected === dir ? 'bg-palamuru-blue text-white border-palamuru-blue shadow' : 'bg-white text-gray-800 border-gray-200 hover:bg-blue-50'}`}
                onClick={() => setSelected(dir)}
              >
                {dir}
              </button>
            ))}
          </div>
        </div>

        {/* Compass and Heading */}
        {selected && (
          <>
            <Compass direction={selected} />
            <div className="flex flex-col items-center">
              <div className="w-full max-w-2xl bg-white rounded-2xl shadow-soft border border-blue-50 p-6 mb-6">
                <h2 className="text-2xl font-serif text-palamuru-blue mb-2">{`Your Plot is ${selected} Facing`}</h2>
                {/* Room Placement Table */}
                <table className="min-w-full text-sm border rounded-xl mb-2">
                  <thead>
                    <tr className="bg-blue-50">
                      <th className="px-3 py-2 text-left font-semibold">Room</th>
                      <th className="px-3 py-2 text-left font-semibold">Recommended Zone</th>
                      <th className="px-3 py-2 text-left font-semibold">Why This Zone?</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vastu.rooms.map((room, i) => (
                      <tr key={i} className="border-b last:border-b-0">
                        <td className="px-3 py-2">{room.name}</td>
                        <td className="px-3 py-2">{room.zone}</td>
                        <td className="px-3 py-2 text-gray-600">{room.tip}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Highlighted Info Block */}
                <div className="bg-yellow-100 border-l-4 border-yellow-400 text-yellow-900 p-4 rounded-xl my-4 text-center text-lg font-medium">
                  🧘 We're aligning your home with principles that boost health, clarity, peace, and prosperity — grounded in both tradition and science.
                </div>
              </div>
            </div>
          </>
        )}

        {/* Prompt if no direction selected */}
        {!selected && (
          <div className="bg-white rounded-2xl shadow-soft border border-blue-50 p-8 mb-10 animate-fade-in text-center">
            <h2 className="text-xl font-serif text-gray-900 mb-2">Tell us your plot direction for customized vastu insights.</h2>
            <p className="text-gray-600">Choose a direction above to view insights.</p>
          </div>
        )}

        {/* How We Blend Tradition & Science */}
        <div className="mb-10">
          <h3 className="text-xl font-serif text-gray-900 mb-4 text-center">How We Blend Tradition & Science</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {PRINCIPLES.map((p, i) => (
              <div key={p.title} className="bg-gradient-to-br from-yellow-50 to-blue-50 rounded-xl border border-blue-100 shadow p-5 flex flex-col items-center text-center animate-slide-up" style={{animationDelay: `${0.1*i}s`, animationFillMode:'forwards'}}>
                <span className="text-3xl mb-2">{p.icon}</span>
                <div className="font-semibold text-gray-800 mt-2 mb-1">{p.title}</div>
                <div className="text-gray-600 text-sm">{p.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Animations (add to your global CSS or Tailwind config):
// .animate-fade-in { animation: fadeIn 0.8s ease both; }
// .animate-slide-up { animation: slideUp 0.7s cubic-bezier(.23,1.01,.32,1) both; }
// @keyframes fadeIn { from { opacity:0; transform:translateY(30px);} to { opacity:1; transform:translateY(0);} }
// @keyframes slideUp { from { opacity:0; transform:translateY(40px);} to { opacity:1; transform:translateY(0);} } 